import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type GetChatQueueResult,
  type GetConversationResult,
  type GetVisitorContextResult,
  type ListCannedResponsesResult,
} from '@whinerp/client';
import { Button, EmptyState, ErrorState, Icon, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatDateTime, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-39 Live chat console.
 *
 * Three things this screen is careful about, and all three are about the
 * operator being mid-sentence when something changes.
 *
 * **The context panel loads independently of the thread.** An operator reads
 * who they are talking to before they type, so the panel is its own query and
 * renders the moment it lands. Bundled with the thread it would arrive after
 * the first reply, which is after it stops being useful.
 *
 * **A send failure keeps the draft.** Always, including when the visitor has
 * left. A paragraph somebody has just typed is worth more than a clean text
 * box, and throwing it away is how an operator learns to compose elsewhere.
 *
 * **A visitor leaving is not an error.** It gets its own message, in its own
 * words, because "something went wrong" invites a retry that cannot work.
 */

type Queue = GetChatQueueResult;
type Thread = GetConversationResult;
type Context = GetVisitorContextResult;
type Canned = ListCannedResponsesResult;

type Tab = 'waiting' | 'active' | 'mine';

function ChatConsoleScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('waiting');
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [failure, setFailure] = useState<{ message: string; visitorLeft: boolean } | null>(null);

  const queue = useQuery<Queue, ApiError>({
    queryKey: ['chat-queue'],
    queryFn: () => api.getChatQueue({}),
  });

  const thread = useQuery<Thread, ApiError>({
    queryKey: ['chat-conversation', selected],
    queryFn: () => api.getConversation({ path: { conversationId: selected ?? '' } }),
    enabled: selected !== null,
  });

  // Its own query, deliberately. The panel is on screen while the messages are
  // still arriving.
  const visitorId = thread.data?.conversation.visitorId ?? null;
  const context = useQuery<Context, ApiError>({
    queryKey: ['chat-visitor', visitorId],
    queryFn: () => api.getVisitorContext({ path: { visitorId: visitorId ?? '' } }),
    enabled: visitorId !== null,
  });

  const canned = useQuery<Canned, ApiError>({
    queryKey: ['canned-responses'],
    queryFn: () => api.listCannedResponses({}),
  });

  const send = useMutation({
    mutationFn: () =>
      api.sendChatReply({
        path: { conversationId: selected ?? '' },
        body: { body: draft },
      }),
    onSuccess: () => {
      setDraft('');
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['chat-conversation', selected] });
      void queryClient.invalidateQueries({ queryKey: ['chat-queue'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : t('error.unexpected');
      /*
       * The draft is NOT cleared here, and that is the whole point.
       *
       * A visitor closing their window while an operator is typing is the
       * common case, not the rare one, and a console that emptied the box
       * would lose a paragraph nobody will retype.
       */
      setFailure({ message, visitorLeft: /closed their window/i.test(message) });
    },
  });

  const end = useMutation({
    mutationFn: (conversationId: string) => api.endConversation({ path: { conversationId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['chat-conversation', selected] });
    },
  });

  // A chat that needs follow-up becomes a case, with its transcript (G-193).
  const toCase = useMutation({
    mutationFn: (conversationId: string) => api.caseFromConversation({ path: { conversationId }, body: {} }),
  });

  const claim = useMutation({
    mutationFn: (conversationId: string) => api.claimConversation({ path: { conversationId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chat-queue'] }),
    onError: (error: unknown) =>
      setFailure({
        message: error instanceof Error ? error.message : t('error.unexpected'),
        visitorLeft: false,
      }),
  });

  if (queue.isError) return <ChatError error={queue.error} retry={() => void queue.refetch()} />;
  if (queue.isPending) return <ChatLoading />;

  const lists: Record<Tab, Queue['waiting']> = {
    waiting: queue.data.waiting,
    active: queue.data.active,
    mine: queue.data.mine,
  };
  const conversations = lists[tab];

  return (
    <div data-component="ChatConsole" className="flex w-full min-w-0 gap-4">
      {/* Left: the queue. */}
      <aside aria-label={t('chat.title')} data-pane="queue" className="flex w-panel-narrow shrink-0 flex-col gap-2">
        <span role="group" aria-label={t('chat.title')} className="flex gap-1">
          {(['waiting', 'active', 'mine'] as const).map((option) => (
            <button
              key={option}
              type="button"
              data-queue-tab={option}
              aria-pressed={tab === option}
              onClick={() => setTab(option)}
              className={
                tab === option
                  ? 'inline-flex h-touch flex-1 items-center justify-center gap-1 rounded-control bg-bronze-tint px-2 text-dense text-bronze-text'
                  : 'inline-flex h-touch flex-1 items-center justify-center gap-1 rounded-control px-2 text-dense text-ink-600 hover:bg-sunken'
              }
            >
              {t(`chat.queue.${option}` as 'chat.queue.waiting')}
              {/* The count on the tab comes from the same list underneath it,
                  so the two cannot disagree. */}
              <Num data-queue-count={option} className="text-meta">
                {formatCount(lists[option].length, format)}
              </Num>
            </button>
          ))}
        </span>

        {conversations.length === 0 ? (
          <ChatEmpty reason="filtered-to-nothing" />
        ) : (
          <ul className="flex flex-col gap-1">
            {conversations.map((conversation) => (
              <li key={conversation.conversationId}>
                <button
                  type="button"
                  data-conversation={conversation.conversationId}
                  aria-pressed={selected === conversation.conversationId}
                  onClick={() => {
                    setSelected(conversation.conversationId);
                    setFailure(null);
                  }}
                  className="flex w-full flex-col gap-0.5 rounded-card border border-rule p-2 text-start hover:bg-sunken"
                >
                  <span className="truncate text-dense text-ink">
                    {conversation.subject ?? conversation.lastMessage ?? t('chat.pick')}
                  </span>
                  <Num className="text-meta text-ink-meta">
                    {t('chat.waitingFor', {
                      values: {
                        duration: formatRelativeDate(conversation.startedAt, format),
                      },
                    })}
                  </Num>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Centre: the conversation. */}
      <section aria-label={t('chat.reply')} data-pane="thread" className="flex min-w-0 flex-1 flex-col gap-2">
        {selected === null ? (
          <p className="text-body text-ink-meta">{t('chat.pick')}</p>
        ) : thread.isPending ? (
          <Skeleton className="h-plot w-full" />
        ) : thread.isError ? (
          <ErrorState
            title={t('state.error')}
            body={thread.error.message}
            onRetry={() => void thread.refetch()}
          />
        ) : (
          <>
            <ol data-thread className="flex flex-1 flex-col gap-2 overflow-y-auto">
              {thread.data.messages.map((message) => (
                <li
                  key={message.messageId}
                  data-message={message.messageId}
                  data-author={message.author}
                  className="flex flex-col gap-0.5 rounded-card border border-rule p-2"
                >
                  <span className="flex items-baseline gap-2 text-meta text-ink-meta">
                    {/* The author in words, never only by which side it sits
                        on: a transcript read aloud has no left and right. */}
                    <span>
                      {message.authorName ??
                        t(`chat.author.${message.author}` as 'chat.author.visitor')}
                    </span>
                    <Num className="text-meta">{formatDateTime(message.sentAt, format)}</Num>
                  </span>
                  <span className="text-body text-ink">{message.body}</span>
                </li>
              ))}
            </ol>

            {/* The visitor-left state, distinct from an error. */}
            {thread.data.conversation.status === 'abandoned' ? (
              <p data-visitor-left className="rounded-card bg-sunken px-3 py-2 text-dense text-ink-meta">
                <Icon name="SignOut" size="inline" decorative /> {t('chat.abandoned')}{' '}
                {t('chat.abandonedHelp')}
              </p>
            ) : thread.data.conversation.status === 'ended' ? (
              <p data-conversation-ended className="rounded-card bg-sunken px-3 py-2 text-dense text-ink-meta">
                {t('chat.ended')}
              </p>
            ) : null}

            {failure ? (
              <p
                role="alert"
                data-send-failed
                data-visitor-left-failure={failure.visitorLeft ? 'true' : 'false'}
                className="rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text"
              >
                {failure.message} {t('chat.draftKept')}
              </p>
            ) : null}

            <div className="flex flex-col gap-1">
              <label className="flex flex-col gap-1 text-dense text-ink">
                <span className="sr-only">{t('chat.reply')}</span>
                <textarea
                  data-reply-draft
                  aria-label={t('chat.reply')}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  className="min-h-btn rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                />
              </label>

              <span className="flex flex-wrap items-center gap-2">
                <Button
                  intent="primary"
                  icon="PaperPlaneTilt"
                  data-send-reply
                  busy={send.isPending}
                  onClick={() => send.mutate()}
                >
                  {t('chat.send')}
                </Button>
                {thread.data.conversation.status === 'waiting' ? (
                  <Button
                    intent="secondary"
                    data-claim
                    onClick={() => claim.mutate(thread.data.conversation.conversationId)}
                  >
                    {t('chat.claim')}
                  </Button>
                ) : null}
                {thread.data.conversation.status === 'active' ? (
                  // Closing is how a conversation leaves the queue. Without
                  // it an operator's list only grows, and `ended` would be a
                  // state nothing could reach.
                  <Button
                    intent="secondary"
                    icon="Check"
                    data-end-conversation
                    busy={end.isPending}
                    onClick={() => end.mutate(thread.data.conversation.conversationId)}
                  >
                    {t('chat.end')}
                  </Button>
                ) : null}
                <Button
                  intent="secondary"
                  data-chat-to-case
                  busy={toCase.isPending}
                  onClick={() => toCase.mutate(thread.data.conversation.conversationId)}
                >
                  {t('chat.toCase')}
                </Button>
                {toCase.data && toCase.variables === thread.data.conversation.conversationId ? (
                  <a data-chat-case={toCase.data.caseId} href={`/cases/${toCase.data.caseId}`} className="text-dense text-bronze underline">
                    {t(toCase.data.created ? 'chat.caseMade' : 'chat.caseExists', { values: { number: toCase.data.number } })}
                  </a>
                ) : null}

                {/* Canned replies insert into the draft rather than sending,
                    so an operator can edit before it goes. */}
                {(canned.data?.responses ?? []).map((response) => (
                  <Button
                    key={response.responseId}
                    intent="ghost"
                    size="small"
                    data-canned={response.shortcut}
                    onClick={() => setDraft((current) => `${current}${current ? '\n' : ''}${response.body}`)}
                  >
                    {response.shortcut}
                  </Button>
                ))}
              </span>
            </div>
          </>
        )}
      </section>

      {/* Right: who they are. Loads on its own. */}
      <aside aria-label={t('chat.context')} data-pane="context" className="flex w-panel-wide shrink-0 flex-col gap-2">
        <SectionLabel>{t('chat.context')}</SectionLabel>
        {visitorId === null ? (
          <p className="text-meta text-ink-meta">{t('chat.pick')}</p>
        ) : context.isPending ? (
          <Skeleton className="h-plot w-full" />
        ) : context.isError ? (
          <p role="alert" className="text-dense text-danger-text">
            {context.error.message}
          </p>
        ) : (
          <Panel>
            <dl data-visitor-context className="flex flex-col gap-2">
              <div>
                <dt className="text-micro uppercase tracking-label text-ink-label">
                  {t('chat.context.location')}
                </dt>
                <dd className="text-body text-ink">
                  {[context.data.city, context.data.country].filter(Boolean).join(', ') ||
                    t('chat.context.unknownLocation')}
                </dd>
              </div>
              <div>
                <dt className="text-micro uppercase tracking-label text-ink-label">
                  {t('chat.context.match')}
                </dt>
                <dd className="text-body text-ink">
                  {context.data.match ? (
                    <a
                      href={`/${context.data.match.kind === 'contact' ? 'contacts' : 'leads'}/${context.data.match.id}`}
                      data-visitor-match
                      className="text-bronze-text underline underline-offset-4"
                    >
                      {context.data.match.name}
                    </a>
                  ) : (
                    /* Says so, rather than leaving the field blank. A blank
                       field is indistinguishable from one that failed to
                       load. */
                    <span data-visitor-unmatched className="text-ink-meta">
                      {t('chat.context.noMatch')}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-micro uppercase tracking-label text-ink-label">
                  {t('chat.context.pages')}
                </dt>
                <dd>
                  {context.data.pages.length === 0 ? (
                    <span className="text-body text-ink-meta">{t('chat.context.noPages')}</span>
                  ) : (
                    <ul data-visitor-pages className="flex flex-col gap-0.5">
                      {context.data.pages.map((page) => (
                        <li key={`${page.url}-${page.viewedAt}`} className="truncate text-dense text-ink">
                          {page.title ?? page.url}
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
              </div>
            </dl>
          </Panel>
        )}
      </aside>
    </div>
  );
}

function ChatEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="ChatText"
      title={t('chat.noConversations')}
      /* The widget's state and a way to its settings: an empty queue with no
         status is indistinguishable from a widget that is switched off. */
      body={`${t('chat.widgetStatus')} ${t('chat.settings')}`}
      primary={{ label: t('empty.action.chatSettings'), icon: 'Gear', href: '/setup' }}
    />
  );
}

function ChatLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-plot w-panel-narrow" />
      <Skeleton className="h-plot flex-1" />
      <Skeleton className="h-plot w-panel-wide" />
    </div>
  );
}

function ChatConsoleError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The chat queue could not be loaded"
      body={error instanceof Error ? error.message : 'The queue did not load.'}
      onRetry={retry}
    />
  );
}

const ChatError = ChatConsoleError;

export const screen = defineScreen({
  id: 'SC-39',
  route: '/chat',
  surface: 'page',
  title: 'Live chat',
  features: ['F-151'],
  entitlement: 'F-151',
  reachedBy: [{ kind: 'nav', item: 'support' }],
  components: [],
  elements: ['ChatConsole'],
  /*
   * The empty state belongs to a tab, not to the screen.
   *
   * The console opens on a queue that has conversations in it, so nothing is
   * empty until somebody picks a tab that is. Declaring it here rather than in
   * `components` means a UI test has to drive the screen until it appears -
   * which is the stronger of the two demands, and it caught that the test
   * asserting it was written with an `if` around it.
   */
  afterInteraction: ['EmptyState'],
  states: { empty: ChatEmpty, loading: ChatLoading, error: ChatConsoleError },
  component: ChatConsoleScreen,
});

export default screen;
